import { Fragment, ReactNode, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { ETransportType } from '../../../common/types';
import { Icon40 } from '../../../components/Icon';
import PriceCalendar from '../../../components/PriceCalendar';
import TripComparator from '../../../components/TripComparator';
import {
  TripComparatorCheapTicketsContext,
  TripComparatorTabs,
} from '../../../components/TripComparator/types';
import { comparePriceHistories } from '../../../components/TripComparator/utils';
import { HearthIcon } from '../../../images/icons/hearth';
import { ReactComponent as IconDoodle } from '../../../images/illustrated/doodle.svg';
import { dayjs } from '../../../libs/day';
import { City } from '../../../services/cities/types';
import { computeCo2g, getTransportId } from '../../../services/co2';
import { PriceCalendarDate } from '../../../services/priceCalendar/types';
import { colors, screens } from '../../../styles/constants';
import {
  BEST_TIME_TO_BOOK_QUOTES,
  FIND_MY_TICKETS_SECTION_ID,
  PRICE_CALENDAR_SECTION_ID,
  SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE,
  TRIP_CO2_QUOTES,
  TRIP_INFORMATION_SECTION_ID,
} from '../constants';
import { generateSeed } from '../helpers/TextGen';
import { DynamicSearchPageInformation, SearchPageType } from '../types';
import {
  canDisplayTripInformationSection,
  getFormattedTripMessages,
} from '../utils';
import messages from './messages';
import TripInformation from './TripInformation';
import TripProviders from './TripProviders';

const Container = styled.div`
  ${tw`flex flex-col mb-big`}
  gap: var(--size-big);

  @media (min-width: ${screens.md}) {
    grid-column-start: 5;
    grid-column-end: 13;
  }

  @media (min-width: ${screens.lg}) {
    grid-column-end: 12;
  }

  @media (min-width: ${screens.xl}) {
    margin-right: -16px;
    grid-column-start: 4;
  }
`;

const Section = styled.section`
  ${tw`flex flex-col`}
  gap: var(--size-medium2);

  @media (min-width: ${screens.md}) {
    gap: var(--size-big);
  }
`;

export const Title = styled.h2`
  ${tw`text-title-xxlarge-m leading-title-xxlarge-m md:text-title-xxlarge md:leading-title-xxlarge font-kyrial text-center`}

  .highlight {
    ${tw`text-primary-400 `}
  }
`;

export const SubTitle = styled.h3`
  ${tw`text-title-xlarge-m leading-title-xlarge-m md:text-title-xlarge md:leading-title-xlarge font-kyrial text-center`}

  .highlight {
    ${tw`text-primary-400 `}
  }
`;

const WidgetWrapperStyle = css`
  ${tw`w-full m-auto`}
  max-width:800px;
`;

const TripComparatorWrapper = styled.div`
  ${WidgetWrapperStyle}
  ${tw`rounded-30 overflow-hidden `}
`;

const PriceCalendarWrapper = styled.div`
  ${WidgetWrapperStyle}
  ${tw`overflow-x-auto`}
`;

const DoodleIcon = styled(IconDoodle)`
  transform: translateY(-5px);
`;

type SearchViewProps = {
  originCity: City;
  destinationCity: City;
  tripData: any;
  dynamicInformation: DynamicSearchPageInformation;
  currentPageType: SearchPageType;
  isMainPageType: boolean;
  intl: any;

  renderLagacyContent: (props: {
    afterCompaniesTableContent?: ReactNode;
  }) => ReactNode;
};

const SearchViewContent = (props: SearchViewProps) => {
  const {
    currentPageType,
    destinationCity,
    dynamicInformation,
    intl,
    isMainPageType,
    originCity,
    renderLagacyContent,
    tripData,
  } = props;
  const pageTransportType = SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE[currentPageType];

  return (
    <Container>
      {isMainPageType && (
        <PriceCalendarSection
          destinationCity={destinationCity}
          intl={intl}
          originCity={originCity}
          pageTransportType={pageTransportType}
        />
      )}

      {canDisplayTripInformationSection(tripData) && (
        <Section id={TRIP_INFORMATION_SECTION_ID}>
          {!isMainPageType && (
            <TripProviders providers={dynamicInformation.companies} />
          )}

          <Title>
            <FormattedHTMLMessage {...messages.tripInfo} />
          </Title>

          <TripInformation
            distanceAsCrowFlies={tripData.distanceAsCrowFlies}
            information={dynamicInformation}
          />

          {isMainPageType && (
            <TripProviders providers={dynamicInformation.companies} />
          )}
        </Section>
      )}
      {renderLagacyContent({
        afterCompaniesTableContent: isMainPageType ? (
          <Fragment>
            <FindMyTicketsSection
              destinationCity={destinationCity}
              distanceAsCrowFlies={tripData.distanceAsCrowFlies}
              intl={intl}
              originCity={originCity}
              pageTransportType={
                currentPageType === SearchPageType.GENERIC
                  ? ETransportType.TRAIN
                  : pageTransportType
              }
            />
          </Fragment>
        ) : undefined,
      })}
    </Container>
  );
};

type FindMyTicketsSectionProps = {
  originCity: City;
  destinationCity: City;
  distanceAsCrowFlies: number;
  pageTransportType: ETransportType | undefined;
  intl: any;
};

const FindMyTicketsSection = (props: FindMyTicketsSectionProps) => {
  const [currentDisplayedWidget, setCurrentDisplayedWidget] =
    useState<TripComparatorTabs | null>(null);
  const [cheapTicketsContext, setCheapTicketsContext] =
    useState<TripComparatorCheapTicketsContext | null>();
  const {
    destinationCity,
    distanceAsCrowFlies,
    intl,
    originCity,
    pageTransportType,
  } = props;

  return (
    <Section id={FIND_MY_TICKETS_SECTION_ID}>
      <Title>
        <FormattedHTMLMessage {...messages.findMyTickets} />

        <HearthIcon
          color={colors['green-500']}
          customStyle={css`
            ${Icon40}
          `}
        />
      </Title>

      <TripComparatorWrapper>
        <TripComparator
          destinationCity={destinationCity}
          distanceAsCrowFlies={distanceAsCrowFlies}
          onCheapTicketsContextChange={setCheapTicketsContext}
          onDisplayedTabChange={setCurrentDisplayedWidget}
          originCity={originCity}
        />
      </TripComparatorWrapper>

      <FindMyTicketsInformation
        cheapTicketsContext={cheapTicketsContext}
        currentDisplayedWidget={currentDisplayedWidget}
        destinationCity={destinationCity}
        distanceAsCrowFlies={distanceAsCrowFlies}
        intl={intl}
        originCity={originCity}
        pageTransportType={pageTransportType}
      />
    </Section>
  );
};

type FindMyTicketsInformationProps = {
  pageTransportType: ETransportType | undefined;
  currentDisplayedWidget: TripComparatorTabs | null;
  cheapTicketsContext: TripComparatorCheapTicketsContext | null;
  originCity: City;
  destinationCity: City;
  distanceAsCrowFlies: number;
  intl: any;
};

const FindMyTicketsInformation = ({
  cheapTicketsContext,
  currentDisplayedWidget,
  destinationCity,
  distanceAsCrowFlies,
  intl,
  originCity,
  pageTransportType,
}: FindMyTicketsInformationProps) => {
  const transportId = getTransportId(pageTransportType);
  const pageSeed = generateSeed(window.location.pathname);
  const transportTypeMessage = intl.formatMessage(
    messages[pageTransportType ?? 'genericTransportType'],
  );

  const formattedTripMessages = getFormattedTripMessages(
    intl,
    originCity,
    destinationCity,
  );

  if (
    currentDisplayedWidget === TripComparatorTabs.CHEAP_TICKETS &&
    cheapTicketsContext
  ) {
    return renderCheapTicketsQuote(
      cheapTicketsContext,
      originCity,
      destinationCity,
      transportTypeMessage,
      formattedTripMessages,
      pageSeed,
    );
  } else if (
    currentDisplayedWidget === TripComparatorTabs.REDUCE_EMISSIONS &&
    transportId
  ) {
    const co2g = computeCo2g(distanceAsCrowFlies, transportId);
    return renderCo2Quote(
      co2g,
      originCity,
      destinationCity,
      transportTypeMessage,
      formattedTripMessages,
      pageSeed,
    );
  }

  return null;
};

const renderCheapTicketsQuote = (
  cheapTicketsContext: TripComparatorCheapTicketsContext,
  originCity: City,
  destinationCity: City,
  transportTypeMessage: string,
  formattedTripMessages: any,
  pageSeed: number,
) => {
  const { highestPriceHistory, lowestPriceHistory } =
    getPriceHistories(cheapTicketsContext);

  if (
    cheapTicketsContext.availableValues.length < 2 ||
    lowestPriceHistory === highestPriceHistory
  ) {
    return (
      <Quote>
        <FormattedHTMLMessage
          {...messages.bestTimeToBookNoData}
          values={{
            origin: originCity.name,
            destination: destinationCity.name,
            transportType: transportTypeMessage,
          }}
        />
      </Quote>
    );
  }

  const comparaison = comparePriceHistories(
    cheapTicketsContext.priceHistories?.[lowestPriceHistory],
    cheapTicketsContext.priceHistories?.[highestPriceHistory],
  );

  return (
    <Quote>
      <FormattedHTMLMessage
        {...messages[
          BEST_TIME_TO_BOOK_QUOTES[pageSeed % BEST_TIME_TO_BOOK_QUOTES.length]
        ]}
        values={{
          origin: originCity.name,
          destination: destinationCity.name,
          transportType: transportTypeMessage,
          formattedTrip1: formattedTripMessages.formattedTrip1Message,
          daysBeforeDeparture: lowestPriceHistory,
          percentageSaved: comparaison.percentageDifference,
          formattedTrip3: formattedTripMessages.formattedTrip3Message,
        }}
      />
    </Quote>
  );
};

const renderCo2Quote = (
  co2g: number,
  originCity: City,
  destinationCity: City,
  transportTypeMessage: string,
  formattedTripMessages: any,
  pageSeed: number,
) => {
  return (
    <Quote>
      <FormattedHTMLMessage
        {...messages[TRIP_CO2_QUOTES[pageSeed % TRIP_CO2_QUOTES.length]]}
        values={{
          origin: originCity.name,
          destination: destinationCity.name,
          transportType: transportTypeMessage,
          formattedTrip1: formattedTripMessages.formattedTrip1Message,
          co2kg: (co2g / 1000).toFixed(2),
          formattedTrip2: formattedTripMessages.formattedTrip2Message,
        }}
      />
    </Quote>
  );
};

const getPriceHistories = (
  cheapTicketsContext: TripComparatorCheapTicketsContext,
) => {
  const highestPriceHistory = cheapTicketsContext.availableValues.reduce(
    (acc, currentValue) => {
      const currentPriceHistory =
        cheapTicketsContext.priceHistories[currentValue];
      const accPriceHistory = cheapTicketsContext.priceHistories[acc];

      if (
        !acc ||
        (currentPriceHistory &&
          currentPriceHistory.medianPrice > accPriceHistory.medianPrice)
      ) {
        return currentValue;
      }
      return acc;
    },
    null,
  );

  const lowestPriceHistory = cheapTicketsContext.availableValues.reduce(
    (acc, currentValue) => {
      const currentPriceHistory =
        cheapTicketsContext.priceHistories[currentValue];
      const accPriceHistory = cheapTicketsContext.priceHistories[acc];

      if (
        !acc ||
        (currentPriceHistory &&
          currentPriceHistory.medianPrice < accPriceHistory.medianPrice)
      ) {
        return currentValue;
      }
      return acc;
    },
    null,
  );

  return { highestPriceHistory, lowestPriceHistory };
};

const Quote = styled.p`
  ${WidgetWrapperStyle}
  ${tw`relative py-medium2 px-medium2  rounded-5 text-p-large-m leading-p-large-m md:text-p-large md:leading-p-large`}

  &:before {
    ${tw`absolute rounded-full bg-primary-500`}
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 8px;
    height: 100%;
  }
`;

type PriceCalendarSectionProps = {
  pageTransportType: ETransportType | undefined;
  originCity: City;
  destinationCity: City;
  intl: any;
};

export const PriceCalendarSection = (props: PriceCalendarSectionProps) => {
  const { destinationCity, intl, originCity, pageTransportType } = props;
  const [cheapestCalendarDate, setCheapestCalendarDate] =
    useState<PriceCalendarDate | null>(null);

  return (
    <Section
      id={PRICE_CALENDAR_SECTION_ID}
      style={{
        display: cheapestCalendarDate ? undefined : 'none',
      }}
    >
      <Title>
        <FormattedHTMLMessage
          {...messages[
            pageTransportType
              ? 'lowestPricesByCustomersSpecific'
              : 'lowestPricesByCustomers'
          ]}
          values={{
            transportType:
              pageTransportType &&
              intl.formatMessage(messages[pageTransportType]),
            origin: originCity.name,
            destination: destinationCity.name,
          }}
        />

        <DoodleIcon />
      </Title>

      <PriceCalendarWrapper>
        <PriceCalendar
          destination={destinationCity}
          origin={originCity}
          setCheapestDate={setCheapestCalendarDate}
        />
      </PriceCalendarWrapper>
      <Quote>
        {cheapestCalendarDate ? (
          <FormattedMessage
            {...messages.nextCheapestTrip}
            values={{
              transportType: (
                <FormattedMessage
                  {...messages[pageTransportType ?? 'genericTransportType']}
                />
              ),
              origin: originCity.name,
              destination: destinationCity.name,
              bestDateToBook: (
                <strong>
                  {dayjs(cheapestCalendarDate.date).format('dddd DD MMMM YYYY')}
                </strong>
              ),
            }}
          />
        ) : (
          <FormattedMessage
            {...messages.noCheapestTripDataAvailable}
            values={{
              origin: originCity.name,
              destination: destinationCity.name,
            }}
          />
        )}
      </Quote>
    </Section>
  );
};

const SearchViewContentIntl = injectIntl(SearchViewContent);
export default SearchViewContentIntl;
